import { generatePath, useNavigate } from 'react-router';
import { ENTERPRISE_APPOINTMENT_STATUS } from 'constants/appointments';
import { ROUTES } from 'constants/routes';
import { useViewSessionReport } from 'hooks/useViewSessionReport';
import { type Appointment } from 'types/appointments';

type ActionableAppointment = Pick<Appointment, 'id' | 'patientId' | 'status'>;

/**
 * Rows containing an element with `data-no-row-action` (DONE appointments, which have no action)
 * drop the pointer cursor that the table applies to clickable rows.
 */
export const APPOINTMENT_TABLE_CLASS_NAME = '[&_tr:has([data-no-row-action])]:cursor-default';

interface UseAppointmentActionOptions {
  detailRoute?: string;
  from?: string;
}

interface UseAppointmentAction<T extends ActionableAppointment> {
  runAppointmentAction: (appointment: T) => void;
  isReportPending: boolean;
}

export const useAppointmentAction = <T extends ActionableAppointment = ActionableAppointment>({
  detailRoute = ROUTES.APPOINTMENT_DETAIL,
  from,
}: UseAppointmentActionOptions = {}): UseAppointmentAction<T> => {
  const navigate = useNavigate();
  const { mutate: viewReport, isPending: isReportPending } = useViewSessionReport();

  const runAppointmentAction = (appointment: T): void => {
    if (appointment.status === ENTERPRISE_APPOINTMENT_STATUS.DONE) return;

    if (appointment.status === ENTERPRISE_APPOINTMENT_STATUS.COMPLETED) {
      viewReport(appointment);
      return;
    }

    navigate(generatePath(detailRoute, { id: appointment.patientId }), {
      state: { appointment, from },
    });
  };

  return { runAppointmentAction, isReportPending };
};
